import {
  Box,
  Button,
  Checkbox,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import { API, graphqlOperation } from "aws-amplify";
import { transferAccountsToFinancialYear } from "../../graphql";
import { useDispatch, useSelector } from "react-redux";
import { showErrorSnackbar, showSuccessSnackbar } from "../snackBar/snakbar";
import { MoneyFormatv3 } from "../../library/MoneyFormat";
import { useThemes } from "../Theme/theme";
import { useFetchDashboardDataQuery } from "../../redux/apis/dashboardApi";
import { useVirtualizer } from "@tanstack/react-virtual";
import { LoadingButton } from "@mui/lab";

function FYClosingBalancelist({ open, setConfirmTransfer }) {
  const { theme } = useThemes();

  const companyReducer = useSelector((state) => state.companyReducer);

  const closingFYDetails = useSelector(
    (state) => state?.closeBookFYStateReducer
  );

  const [accountList, setAccountList] = useState([]);
  const [loading, setLoading] = useState(false);

  const dashboardVariables =
    closingFYDetails && companyReducer
      ? {
          startdate: closingFYDetails?.FromFYObj?.startdate,
          enddate: closingFYDetails?.FromFYObj?.enddate,
          openingbalance: 1,
          companyid: companyReducer.companyDetails?.companyid,
          financialyearid: closingFYDetails?.FromFYObj?.financialyearid,
        }
      : null;

  const areQueryVariablesValid = dashboardVariables
    ? Object.values(dashboardVariables).every(
        (value) => value !== undefined && value !== null
      )
    : false;

  const {
    data: dashboardata,
    isLoading,
    refetch: refetchDashboard,
  } = useFetchDashboardDataQuery(dashboardVariables, {
    skip: !areQueryVariablesValid,
    refetchOnMountOrArgChange: true,
  });


  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const initialPayloadState = {
    accountIds: "",
    accountGroupIds: "",
  };
  const [payload, setPayload] = useState(initialPayloadState);

  const handleClose = () => setConfirmTransfer(false);

  async function fetchAccountsList(data) {
    const companyDetails =
      companyReducer?.companyDetails?.relatedcompanydetailsnew?.[0] || {};
    const { registrationtype } = companyDetails;
    const preventGroup = [];

    if (registrationtype === 1) {
      preventGroup.push(31, 32, 33, 8);
    }

    const filterLedgerAccounts = data?.filter(
      (obj) =>
        obj.type === "Account" &&
        obj?.accounttypecd === "BS" &&
        obj.accountid !== 12 &&
        !preventGroup.includes(obj?.immediate_parent_group_id)
    );

    setAccountList(filterLedgerAccounts);
    const allAccountIds = filterLedgerAccounts.map(
      (filteredObjR) => filteredObjR.accountid
    );
    setSelectedAccounts(allAccountIds);
  }

  useEffect(() => {
    if (dashboardata) {
      fetchAccountsList(dashboardata);
    }
  }, [dashboardata]);

  async function transferClosingBalance() {
    try {
      setLoading(true);

      // Validate required fields
      if (
        !closingFYDetails?.FromFYObj?.financialyearid ||
        !closingFYDetails?.ToFYObj?.financialyearid
      ) {
        throw new Error("Financial year details are missing");
      }

      const transferParams = {
        excludedaccountidlist: payload.accountIds || "",
        excludedaccountgroupidlist: "",
        ledger_transfer_option: 3,
        party_transfer_option: 3,
        item_transfer_option: 3,
        transfer_groups_ledgers: 1,
        ledger_closing_balance: 1,
        party_closing_balance: 1,
        from_financialyearid: closingFYDetails.FromFYObj.financialyearid,
        to_financialyearid: closingFYDetails.ToFYObj.financialyearid,
      };

      const reTransferRes = await API.graphql(
        graphqlOperation(transferAccountsToFinancialYear, transferParams)
      );

      const statusCode =
        reTransferRes?.data?.transferAccountsToFinancialYear?.statuscode;

      if (!statusCode) {
        throw new Error("Invalid response from server");
      }

      if (statusCode !== "Success") {
        throw new Error(
          reTransferRes?.data?.transferAccountsToFinancialYear?.message ||
            "Failed to transfer closing balance. Please try again or contact support if the issue persists."
        );
      }

      showSuccessSnackbar(
        "Closing balances successfully transferred to the next financial year."
      );

      // Reset state
      setPayload(initialPayloadState);
      setSelectedAccounts([]);
      setConfirmTransfer(false);
    } catch (error) {
      showErrorSnackbar(error?.message || "An unexpected error occurred");
      setConfirmTransfer(false);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectAll = (e) => {
    const allAccounts = accountList.map((obj) => obj.accountid);
    const unrestrictedAccounts = accountList
      .filter((obj) => obj.restrictedflag === 0)
      .map((obj) => obj.accountid);

    if (e.target.checked) {
      setSelectedAccounts(allAccounts);
      setPayload((prev) => ({
        ...prev,
        accountIds: "",
      }));
    } else {
      setPayload((prev) => ({
        ...prev,
        accountIds: unrestrictedAccounts.join(","),
      }));

      const restrictedAccounts = allAccounts.filter(
        (id) => !unrestrictedAccounts.includes(id)
      );
      setSelectedAccounts(restrictedAccounts);
    }
  };

  const handleSelectOne = useCallback((id, checked) => {
    setSelectedAccounts((prevState) =>
      checked
        ? [...prevState, id]
        : prevState.filter((accountId) => accountId !== id)
    );

    setPayload((prev) => {
      const accountIdsArray = prev.accountIds ? prev.accountIds.split(",") : [];
      if (checked) {
        const index = accountIdsArray.indexOf(id.toString());
        if (index > -1) accountIdsArray.splice(index, 1);
      } else {
        accountIdsArray.push(id.toString());
      }
      return { ...prev, accountIds: accountIdsArray.join(",") };
    });
  }, []);

  const isDarkMode = theme?.palette?.TmId.id.toString().includes("9");
  const borderRightColor = isDarkMode ? "#26262F" : "#ffffff";
  const innerBorderRightColor = isDarkMode
    ? theme.palette.primary.black
    : "#eef0f0";

  const parentRef = useRef(null);

  const memorizedData = useMemo(() => {
    return accountList;
  }, [accountList]);

  const rowVirtualizer = useVirtualizer({
    count: memorizedData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div>
      <Drawer anchor={"right"} open={open} onClose={handleClose}>
        <Box
          sx={{
            mx: { xs: "2px", lg: 1 },
            width: {
              xs: "100%",
              sm: "80vw",
              md: "60vw",
              lg: "40vw",
            },
            height: "98vh",
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/*  Header */}
          <Box
            sx={{
              mx: { xs: "2px", lg: 1 },
              mt: 1,
              bgcolor: "primary.main",
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pl: 2,
              color: "white",
              fontWeight: "bold",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <Typography variant="h1">Ledger having closing balance</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>

          {/* Sticky Table Header */}
          <Box
            sx={{
              mx: { xs: "2px", lg: 1 },
              mt: "1px",
              position: "sticky",
              top: 40,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                bgcolor: "primary.main",
                color: "white",
                height: 40,
              }}
            >
              {/* Checkbox Header */}
              <Box
                sx={{
                  width: "5%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid",
                  borderRightColor: borderRightColor,
                }}
              >
                <Checkbox
                  color="secondary"
                  size={isSmallScreen ? "small" : "medium"}
                  checked={selectedAccounts.length === memorizedData.length}
                  onClick={(e) => handleSelectAll(e)}
                  sx={{
                    "& svg": {
                      fontSize: {
                        xs: "1rem",
                        sm: "1.2rem",
                        md: "1.4rem",
                      },
                    },
                    color: "white",
                    "&.Mui-checked": {
                      color: "white",
                    },
                  }}
                />
              </Box>

              {/* Accounts Header */}
              <Box
                sx={{
                  width: "55%",
                  display: "flex",
                  alignItems: "center",
                  borderRight: "1px solid",
                  borderRightColor: borderRightColor,
                  pl: 2,
                }}
              >
                <Typography variant="h1">Accounts</Typography>
              </Box>

              {/* Closing Balance Header */}
              <Box
                sx={{
                  width: "40%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pl: 2,
                }}
              >
                <Typography variant="h1">Closing Balance</Typography>
              </Box>
            </Box>
          </Box>

          {/* Scrollable virtualized rows */}
          <Box
            ref={parentRef}
            sx={{
              mx: { xs: "2px", lg: 1 },
              flex: 1,
              overflow: "auto",
              border: "1px solid rgb(218, 217, 217)",
            }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = memorizedData[virtualRow.index];
                return (
                  <div
                    key={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      borderBottom: "1px solid rgb(218, 217, 217)",
                      transform: `translateY(${virtualRow.start}px)`,
                      boxSizing: "border-box",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        height: "100%",
                        bgcolor: virtualRow.index % 2 ? "#f9f9f9" : "#fff",
                        borderBottom: "1px solid rgb(218, 217, 217)",
                      }}
                    >
                      {/* Checkbox Cell */}
                      <Box
                        sx={{
                          width: "5%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRight: "2px solid ",
                          borderRightColor: theme?.palette?.TmId.id
                            .toString()
                            .includes("9")
                            ? theme.palette.primary.black
                            : "#eef0f0",
                        }}
                      >
                        <Tooltip
                          title="Default accounts cannot be unselected"
                          arrow
                          disableHoverListener={item.restrictedflag === 0}
                        >
                          <Checkbox
                            size={isSmallScreen ? "small" : "medium"}
                            sx={{
                              "& svg": {
                                fontSize: {
                                  xs: "1rem",
                                  sm: "1.2rem",
                                  md: "1.4rem",
                                },
                              },
                            }}
                            disabled={item.restrictedflag === 1}
                            checked={selectedAccounts?.includes(
                              item?.accountid
                            )}
                            onChange={(e) =>
                              handleSelectOne(item?.accountid, e.target.checked)
                            }
                          />
                        </Tooltip>
                      </Box>

                      {/* Account Name Cell */}
                      <Box
                        sx={{
                          width: "57%",
                          display: "flex",
                          alignItems: "center",
                          pl: 2,
                          borderRight: "2px solid ",
                          borderRightColor: theme?.palette?.TmId.id
                            .toString()
                            .includes("9")
                            ? theme.palette.primary.black
                            : "#eef0f0",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.85rem",
                              md: "0.95rem",
                              lg: "1rem",
                            },
                            fontWeight: 500,
                          }}
                        >
                          {item?.accountname}
                        </Typography>
                      </Box>

                      {/* Closing Balance Cell */}
                      <Box
                        sx={{
                          width: "40%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          pr: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.85rem",
                              md: "0.95rem",
                            },
                            textAlign: "right",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          ₹ {MoneyFormatv3(item?.balance)}
                        </Typography>
                      </Box>
                    </Box>
                  </div>
                );
              })}
            </div>
          </Box>

          <Stack
            sx={{ mt: 4 }}
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"end"}
            spacing={{ xs: 1, sm: 2 }}
          >
            <Button
              variant="outlined"
              fullWidth={isSmallScreen}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth={isSmallScreen}
              onClick={transferClosingBalance}
              loading={loading}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </Box>
      </Drawer>
    </div>
  );
}

export default FYClosingBalancelist;
